import React from 'react';
import { useLocale } from '../../../contexts/LocaleContext.jsx';
import { cardSkinLook } from '../../../_helpers/cardSkin.js';
import { NAME_PAINT_SURFACES } from '../../../_helpers/namePaint.js';
import { fxTint, rarityLabelKey, rarityTagKind } from '../../../_helpers/shopPreview.js';
import NowPlayingCard from '../stream/NowPlayingCard.jsx';
import '../../../styles/ListenerProfileCard.css';
import PaintedName from '../stream/PaintedName.jsx';

/** A skin's rarity pill ("Legendary"), in the listener card's rarity colours. */
export const ShopRarityTag = ({ rarity }) => {
  const { t } = useLocale();
  return (
    <span className={`listener-card-tag listener-card-tag--${rarityTagKind(rarity)} skin-shop-rarity`}>
      {t(rarityLabelKey(rarity))}
    </span>
  );
};

/**
 * A now-playing card skin as it will look: a NowPlayingCard in that skin,
 * playing "Your song here" by "Any artist", requested by the viewer. It is
 * a picture (`decorative`): the record spins only while its tile or stage is
 * hovered, and none has the collapse button, the large ones (the featured
 * item, the try-on) included.
 */
export const CardPreview = ({ skin, ownName }) => {
  const { t } = useLocale();
  return (
    <NowPlayingCard
      song={{ title: t('shop.sampleSong.title'), artist: t('shop.sampleSong.artist'), requestedBy: ownName }}
      skin={cardSkinLook(skin)}
      decorative
    />
  );
};

/**
 * A name paint as it will look: the viewer's name painted large, a chat row
 * ("BackRowBecky requested Your song here") on the chat surface and the
 * now-playing card's Request row on its surface, each with that surface's
 * contrast lifting (PaintedName).
 */
export const PaintPreview = ({ paint, ownName, large = false }) => {
  const { t } = useLocale();
  return (
    <div className="shop-paint-preview">
      {large && <span className="shop-preview-caption">{t('shop.preview.namePainted')}</span>}
      <PaintedName name={ownName} paint={paint} surface={NAME_PAINT_SURFACES.chat} className="shop-paint-preview-name" />
      <span className="shop-paint-preview-chat">
        <PaintedName name={ownName} paint={paint} surface={NAME_PAINT_SURFACES.chat} className="shop-paint-preview-inline" />
        {' '}
        <span>{t('shop.preview.requested', { title: t('shop.sampleSong.title') })}</span>
      </span>
      <span className="shop-paint-preview-np">
        <span className="shop-paint-preview-np-label">{t('stream.nowPlaying.rows.request')}</span>
        {' '}
        <PaintedName name={ownName} paint={paint} surface={NAME_PAINT_SURFACES.nowPlaying} className="shop-paint-preview-inline" />
      </span>
    </div>
  );
};

const BOLT = 'M13 2L4 14h7l-1 8 9-12h-7l1-8z';

/**
 * A vibe set or boost animation as a lightweight CSS loop, tinted from its FX
 * package (fxTint): a small queue box of three album tiles, the middle one the
 * sample card, with the vibe glowing and pulsing on it, or boosts rising off
 * it. The real engine plays only in the large try-on preview (FxPreview).
 * Still under reduce motion and Photosensitivity mode: the page's
 * `skin-shop-page--still` stops every loop.
 */
export const FxLoopPreview = ({ skin }) => {
  const { t } = useLocale();
  const tint = fxTint(skin);
  const boost = skin.type === 'boost_animation';
  return (
    <div
      className={`shop-fx shop-fx--${boost ? 'boost' : 'vibe'}`}
      style={{ '--shop-fx-a': tint.a, '--shop-fx-b': tint.b }}
      data-testid="skin-shop-fx-loop"
    >
      {!boost && <span className="shop-fx-anim shop-fx-sweep" />}
      <span className="shop-fx-album" />
      <span className="shop-fx-card">
        <span className="shop-fx-anim shop-fx-glow" />
        <span className="shop-fx-anim shop-fx-ring" />
        <span className="shop-fx-anim shop-fx-ring shop-fx-ring--late" />
        <span className="shop-fx-cover" />
        {boost && [1, 2, 3].map((n) => (
          <svg key={n} className={`shop-fx-anim shop-fx-bolt shop-fx-bolt--${n}`} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
            <path d={BOLT} />
          </svg>
        ))}
      </span>
      <span className="shop-fx-album" />
      <span className="shop-fx-caption">{t(boost ? 'shop.preview.boost' : 'shop.preview.queue')}</span>
    </div>
  );
};

/**
 * An item's live preview on a stage of its own, the same per type wherever it
 * shows (ENG-894): a now-playing card skin as the card, a name paint as the
 * viewer's painted name, a vibe set or boost animation as its queue loop.
 * `size` is 'tile' (4:3), 'drop' (16:9) or 'large' (the featured item); it
 * scales with its box (CSS container queries). It is one picture to screen
 * readers ("How Event Horizon looks"). `soldOut` stamps it Sold out.
 */
const ShopPreview = ({ item, ownName, size = 'tile', soldOut = false }) => {
  const { t } = useLocale();
  const { skin } = item;
  const large = size === 'large';
  let body = null;
  if (skin.type === 'now_playing_card') body = <CardPreview skin={skin} ownName={ownName} />;
  else if (skin.type === 'name_paint') body = <PaintPreview paint={skin.paint} ownName={ownName} large={large} />;
  else if (skin.type === 'vibe_set' || skin.type === 'boost_animation') body = <FxLoopPreview skin={skin} />;
  return (
    <div
      className={`shop-stage shop-stage--${size} shop-stage--${skin.type}`}
      role="img"
      aria-label={t('shop.preview.label', { name: skin.name })}
      data-testid="skin-shop-preview"
      data-preview-type={skin.type}
    >
      <div className="shop-stage-body">{body}</div>
      {soldOut && (
        <span className="shop-stage-stamp" aria-hidden="true">
          <span>{t('shop.soldOut')}</span>
        </span>
      )}
    </div>
  );
};

export default ShopPreview;
